import "@/App.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Phone, CheckCircle, Shield, Star, Zap, ChevronRight, Loader2, Clock, Users, Award, HelpCircle, ChevronDown, Camera, X } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company info
const PHONE = "085 801 9623";
const PHONE_LINK = "tel:+353858019623";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_house-rewire/artifacts/7meozpw2_LOGO%20-%20NO%20BACKGROUND.png";
const HERO_URL = "https://images.unsplash.com/photo-1692052664566-477579a08e8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxlbGVjdHJpYyUyMGNhciUyMGhvbWUlMjBjaGFyZ2luZyUyMGRyaXZld2F5fGVufDB8fHx8MTc3MzU4NTAyMHww&ixlib=rb-4.1.0&q=85";

// GA4 tracking helper
const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
};

// Track phone clicks
const trackPhoneClick = (location) => {
  trackEvent('click_to_call', {
    link_location: location,
    phone_number: PHONE
  });
};

// Charger data
const CHARGERS = [
  {
    id: "bg-sync",
    name: "BG Sync EV",
    priceBefore: "€1,295",
    priceAfter: "€995",
    grant: "300",
    features: ["7.5m Cable", "Tethered or Untethered", "Smart App Control", "3 Year Warranty"],
    popular: false,
  },
  {
    id: "zappi-glo",
    name: "MyEnergi Zappi GLO",
    priceBefore: "€1,495",
    priceAfter: "€1,195",
    grant: "300",
    features: ["6.5m Cable", "Solar Compatible", "Tethered or Untethered", "3 Year Warranty"],
    popular: false,
  },
  {
    id: "zappi",
    name: "MyEnergi Zappi V2.1",
    priceBefore: "€1,695",
    priceAfter: "€1,395",
    grant: "300",
    features: ["6.5m Cable", "Solar Compatible", "Tethered or Untethered", "LCD Display", "3 Year Warranty"],
    popular: true,
  },
];

// Lead value per charger (full installed price)
const CHARGER_VALUES = { "bg-sync": 1295, "zappi-glo": 1495, "zappi": 1695, "unsure": 1295 };

// FAQ data
const FAQS = [
  {
    q: "How long does installation take?",
    a: "Most installations are completed in 2-4 hours. We'll agree on a time that suits you."
  },
  {
    q: "Do you help with the SEAI grant?",
    a: "Yes! We handle all the paperwork. You pay the full price upfront, then receive €300 back from SEAI within 4-6 weeks."
  },
  {
    q: "What's included in the price?",
    a: "The price includes your charger, full installation, electrical certification, and up to 5m of cable run from your fuseboard to the charger location."
  },
  {
    q: "Are there any potential extra costs?",
    a: "In some properties, additional work may be needed. Common extras include: cable runs longer than 5m from the fuseboard, meter tail upgrades (if undersized), earth rod installation or upgrade, and bonding to the hot press. We'll identify any extras during your free site survey before any work begins – no surprises."
  },
  {
    q: "Which charger should I choose?",
    a: "The BG Sync is perfect for most homes. Choose the Zappi if you have solar panels or want the premium LCD display."
  }
];

// Header Component
const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0f0f10]/90 border-b border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16 md:h-20">
        <img 
          src={LOGO_URL} 
          alt="LK Electrical & Security" 
          className="h-10 md:h-12 w-auto"
          data-testid="company-logo"
        />
        <a 
          href={PHONE_LINK}
          onClick={() => trackPhoneClick('header')}
          className="flex items-center gap-2 text-[#F5C518] font-bold text-lg md:text-xl hover:text-[#FFD54F] transition-colors"
          data-testid="header-phone"
          aria-label={`Call us at ${PHONE}`}
        >
          <Phone className="w-5 h-5" />
          <span className="hidden sm:inline">{PHONE}</span>
        </a>
      </div>
    </div>
  </header>
);

// Lead Form Component
const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    eircode: "",
    charger_preference: "unsure",
    prewired: "no",
    whatsapp_consent: true,
    fuseboard_image: null,
    meter_image: null,
    location_image: null,
  });
  const [previews, setPreviews] = useState({
    fuseboard_image: null,
    meter_image: null,
    location_image: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Compress and resize image
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for resizing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 1200px width or height)
          const maxSize = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with 0.7 quality (good balance of size/quality)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          setFormData(prev => ({ ...prev, [fieldName]: compressedBase64 }));
          setPreviews(prev => ({ ...prev, [fieldName]: compressedBase64 }));
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
    setPreviews(prev => ({ ...prev, [fieldName]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/leads`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        
        // Track conversion in GA4 (full installed price as value)
        trackEvent('generate_lead', {
          currency: 'EUR',
          value: CHARGER_VALUES[formData.charger_preference] || 1295,
          charger_type: formData.charger_preference,
        });
        
        // Also fire a custom conversion event for Google Ads
        trackEvent('form_submission', {
          form_name: 'ev_charger_quote',
          charger_preference: formData.charger_preference,
        });
        
        toast.success("Quote request submitted!", {
          description: "We'll be in touch within 24 hours.",
        });
      }
    } catch (error) {
      toast.error("Something went wrong", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-[#18181b] p-6 md:p-8 rounded-2xl border border-[#27272a] text-center" data-testid="form-success">
        <div className="w-16 h-16 bg-[#F5C518]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-[#F5C518]" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
        <p className="text-gray-400 mb-6">We'll be in touch within 24 hours to arrange your free site survey.</p>
        <a 
          href={PHONE_LINK}
          onClick={() => trackPhoneClick('success_message')}
          className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-3 px-6 rounded-lg transition-all"
          data-testid="success-call-btn"
        >
          <Phone className="w-5 h-5" />
          Need it sooner? Call now
        </a>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className="bg-[#18181b] p-6 md:p-8 rounded-2xl border border-[#27272a] shadow-2xl"
      data-testid="lead-form"
    >
      <div className="flex items-center gap-2 text-[#F5C518] text-sm font-semibold mb-4">
        <Clock className="w-4 h-4" />
        <span>Free quote in 24 hours</span>
      </div>
      
      <h3 className="text-xl md:text-2xl font-bold text-white mb-1">Get Your Free Quote</h3>
      <p className="text-gray-400 text-sm mb-6">No obligation. We'll call to discuss your installation.</p>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
              data-testid="form-name"
              aria-label="Your name"
            />
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="Your phone number"
              className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
              data-testid="form-phone"
              aria-label="Your phone number"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
              data-testid="form-email"
              aria-label="Your email address"
            />
          </div>
          
          <div>
            <label htmlFor="eircode" className="block text-sm font-medium text-gray-300 mb-1">Eircode</label>
            <input
              type="text"
              id="eircode"
              name="eircode"
              value={formData.eircode}
              onChange={handleChange}
              placeholder="e.g. D02 XY45"
              className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all uppercase"
              data-testid="form-eircode"
              aria-label="Your Eircode"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="charger_preference" className="block text-sm font-medium text-gray-300 mb-1">Charger Preference</label>
          <select
            id="charger_preference"
            name="charger_preference"
            value={formData.charger_preference}
            onChange={handleChange}
            className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
            data-testid="form-charger-preference"
            aria-label="Select your charger preference"
          >
            <option value="unsure">Not sure yet - advise me</option>
            <option value="bg-sync">BG Sync EV — €1,295 (€995 after grant)</option>
            <option value="zappi-glo">MyEnergi Zappi GLO — €1,495 (€1,195 after grant)</option>
            <option value="zappi">MyEnergi Zappi V2.1 — €1,695 (€1,395 after grant)</option>
          </select>
        </div>

        <div>
          <label htmlFor="prewired" className="block text-sm font-medium text-gray-300 mb-1">Is your home pre-wired for an EV charger?</label>
          <select
            id="prewired"
            name="prewired"
            value={formData.prewired}
            onChange={handleChange}
            className="form-input bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
            data-testid="form-prewired"
            aria-label="Is your home pre-wired for an EV charger"
          >
            <option value="no">No / Not sure</option>
            <option value="yes">Yes, it's pre-wired</option>
          </select>
        </div>
        
        {/* Image Upload Section */}
        <div className="pt-4 border-t border-[#27272a]">
          <p className="text-sm font-medium text-gray-300 mb-1">Upload Photos <span className="text-gray-500">(Optional)</span></p>
          <p className="text-xs text-gray-500 mb-4">Help us give you an accurate quote by uploading photos of your fuseboard, meter, and preferred installation location.</p>
          
          <div className="grid grid-cols-3 gap-3">
            {/* Fuseboard */}
            <div>
              <label className="block text-xs text-gray-400 mb-2 text-center">Fuseboard</label>
              <div className="relative">
                {previews.fuseboard_image ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden border border-[#3f3f46]">
                    <img src={previews.fuseboard_image} alt="Fuseboard" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage('fuseboard_image')}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      data-testid="remove-fuseboard-image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-[#3f3f46] hover:border-[#F5C518] flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#27272a]/50">
                    <Camera className="w-6 h-6 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500">Add</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'fuseboard_image')}
                      className="hidden"
                      data-testid="upload-fuseboard"
                    />
                  </label>
                )}
              </div>
            </div>
            
            {/* Meter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2 text-center">Meter</label>
              <div className="relative">
                {previews.meter_image ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden border border-[#3f3f46]">
                    <img src={previews.meter_image} alt="Meter" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage('meter_image')}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      data-testid="remove-meter-image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-[#3f3f46] hover:border-[#F5C518] flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#27272a]/50">
                    <Camera className="w-6 h-6 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500">Add</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'meter_image')}
                      className="hidden"
                      data-testid="upload-meter"
                    />
                  </label>
                )}
              </div>
            </div>
            
            {/* Installation Location */}
            <div>
              <label className="block text-xs text-gray-400 mb-2 text-center">Location</label>
              <div className="relative">
                {previews.location_image ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden border border-[#3f3f46]">
                    <img src={previews.location_image} alt="Location" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage('location_image')}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      data-testid="remove-location-image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-[#3f3f46] hover:border-[#F5C518] flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#27272a]/50">
                    <Camera className="w-6 h-6 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500">Add</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'location_image')}
                      className="hidden"
                      data-testid="upload-location"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* WhatsApp / phone consent */}
        <div className="pt-4 border-t border-[#27272a]">
          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="whatsapp_consent">
            <input
              type="checkbox"
              id="whatsapp_consent"
              name="whatsapp_consent"
              required
              checked={formData.whatsapp_consent}
              onChange={(e) => setFormData({ ...formData, whatsapp_consent: e.target.checked })}
              className="mt-0.5 w-5 h-5 rounded border-[#3f3f46] bg-[#27272a] text-[#F5C518] focus:ring-2 focus:ring-[#F5C518] focus:ring-offset-0 cursor-pointer accent-[#F5C518]"
              data-testid="form-whatsapp-consent"
            />
            <span className="text-sm text-gray-500 group-hover:text-gray-400 transition-colors">
              I consent to being contacted regarding my quote via Phone/WhatsApp
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(245,197,24,0.3)] hover:shadow-[0_0_30px_rgba(245,197,24,0.5)] transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="form-submit"
          aria-label="Get your free quote"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Get Your Free Quote
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
        
        <p className="text-gray-500 text-xs text-center mt-3">
          Your details are safe. We never share your information.
        </p>
      </div>
    </form>
  );
};

// Hero Section - No form, just strong value prop
const HeroSection = () => (
  <section 
    className="hero-bg min-h-[85vh] pt-20 md:pt-24 pb-12 md:pb-16 flex items-center"
    style={{ backgroundImage: `url('${HERO_URL}')` }}
    data-testid="hero-section"
  >
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
        {/* Urgency badge */}
        <div className="inline-flex items-center gap-2 bg-[#F5C518]/20 text-[#F5C518] text-sm font-semibold px-4 py-2 rounded-full mb-6">
          <Zap className="w-4 h-4" />
          Limited slots this month – Book now
        </div>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
          Home EV Charger
          <span className="block text-[#F5C518]">From €995 Installed</span>
        </h1>
        
        <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          Professional installation by Safe Electric registered electricians. 
          Price includes charger, installation & SEAI grant.
        </p>
        
        {/* Social proof */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-10">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-[#27272a] border-2 border-[#0f0f10] flex items-center justify-center">
                <Star className="w-4 h-4 text-[#F5C518]" />
              </div>
            </div>
            <span className="text-white font-semibold">5.0</span>
            <span className="text-gray-400">on Google</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Users className="w-5 h-5 text-[#F5C518]" />
            <span><span className="text-white font-semibold">200+</span> installs completed</span>
          </div>
        </div>
        
        {/* Price cards */}
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-3 max-w-2xl mx-auto mb-4">
          {CHARGERS.map((charger) => (
            <div
              key={charger.id}
              className={`relative bg-[#18181b]/90 backdrop-blur-sm rounded-xl p-4 border transition-all text-center ${
                charger.popular ? "border-[#F5C518]/50" : "border-[#27272a] hover:border-[#F5C518]/50"
              }`}
            >
              {charger.popular && (
                <div className="absolute -top-2 -right-2 bg-[#F5C518] text-black text-xs font-bold px-2 py-1 rounded">
                  POPULAR
                </div>
              )}
              <p className="text-gray-400 text-sm mb-2">{charger.name}</p>
              <p className="text-gray-500 text-xs">Installation price <span className="text-gray-300 font-semibold">{charger.priceBefore}</span></p>
              <p className="text-[#F5C518]/90 text-xs mt-0.5 font-medium">{charger.features[0]}</p>
              <div className="mt-1.5 pt-1.5 border-t border-[#27272a]">
                <p className="text-2xl font-bold text-[#F5C518]">{charger.priceAfter}</p>
                <p className="text-gray-500 text-xs">after €{charger.grant} SEAI grant</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-gray-500 text-sm mb-10">All prices include charger, installation & certification. We handle the SEAI grant paperwork for you.</p>
        
        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="#quote-form"
            className="bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(245,197,24,0.3)] hover:shadow-[0_0_30px_rgba(245,197,24,0.5)] transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            data-testid="hero-cta"
          >
            Get Your Free Quote
            <ChevronRight className="w-5 h-5" />
          </a>
          <a 
            href={PHONE_LINK}
            onClick={() => trackPhoneClick('hero')}
            className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 font-bold py-4 px-8 rounded-lg transition-all flex items-center justify-center gap-2"
            data-testid="hero-call"
          >
            <Phone className="w-5 h-5" />
            {PHONE}
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Trust Section
const TrustSection = () => (
  <section className="py-10 md:py-14 bg-[#18181b]" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <div className="trust-badge bg-[#0f0f10] rounded-xl p-4 md:p-5 text-center border border-[#27272a]" data-testid="trust-safe-electric">
          <Shield className="w-7 h-7 text-[#F5C518] mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">Safe Electric Registered</p>
        </div>
        <div className="trust-badge bg-[#0f0f10] rounded-xl p-4 md:p-5 text-center border border-[#27272a]" data-testid="trust-insured">
          <CheckCircle className="w-7 h-7 text-[#F5C518] mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">Fully Insured</p>
        </div>
        <div className="trust-badge bg-[#0f0f10] rounded-xl p-4 md:p-5 text-center border border-[#27272a]" data-testid="trust-warranty">
          <Award className="w-7 h-7 text-[#F5C518] mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">3 Year Warranty</p>
        </div>
        <div className="trust-badge bg-[#0f0f10] rounded-xl p-4 md:p-5 text-center border border-[#27272a]" data-testid="trust-fixed">
          <Zap className="w-7 h-7 text-[#F5C518] mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">Fixed Price – No Surprises</p>
        </div>
      </div>
    </div>
  </section>
);

// What's Included Section
const WhatsIncluded = () => (
  <section className="py-12 md:py-16 bg-[#0f0f10]" data-testid="whats-included">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">
        What's Included in Your Installation
      </h2>
      
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <div className="text-center">
          <div className="w-12 h-12 bg-[#F5C518]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-6 h-6 text-[#F5C518]" />
          </div>
          <h3 className="text-white font-semibold mb-2">Your Choice of Charger</h3>
          <p className="text-gray-400 text-sm">BG Sync EV or Myenergi Zappi – both premium quality with smart app control</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-[#F5C518]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-[#F5C518]" />
          </div>
          <h3 className="text-white font-semibold mb-2">Professional Installation</h3>
          <p className="text-gray-400 text-sm">Safe Electric certified work with full electrical certification included</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-[#F5C518]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-[#F5C518]" />
          </div>
          <h3 className="text-white font-semibold mb-2">SEAI Grant Assistance</h3>
          <p className="text-gray-400 text-sm">We handle the paperwork – you receive €300 back from SEAI</p>
        </div>
      </div>
    </div>
  </section>
);

// FAQ Item Component
const FAQItem = ({ faq, isOpen, onClick }) => (
  <div 
    className="border-b border-[#27272a] last:border-b-0"
    data-testid={`faq-item-${faq.q.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
  >
    <button
      onClick={onClick}
      className="w-full py-4 flex items-center justify-between text-left"
      aria-expanded={isOpen}
    >
      <span className="text-white font-medium pr-4">{faq.q}</span>
      <ChevronDown className={`w-5 h-5 text-[#F5C518] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="pb-4 text-gray-400 text-sm animate-fade-in">
        {faq.a}
      </div>
    )}
  </div>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  
  return (
    <section className="py-12 md:py-16 bg-[#18181b]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          <HelpCircle className="w-6 h-6 text-[#F5C518]" />
          <h2 className="text-2xl md:text-3xl font-bold text-white">Common Questions</h2>
        </div>
        
        <div className="bg-[#0f0f10] rounded-2xl border border-[#27272a] p-4 md:p-6">
          {FAQS.map((faq, index) => (
            <FAQItem 
              key={index} 
              faq={faq} 
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section with Form
const CTASection = () => (
  <section className="py-16 md:py-20 bg-[#0f0f10] scroll-mt-24" id="get-quote" data-testid="cta-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Charge at Home?
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Join 200+ happy customers across Dublin. Get your free, no-obligation quote today.
          </p>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#F5C518] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">Free site survey</span>
                <p className="text-gray-500 text-sm">We assess your setup and confirm the fixed price</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#F5C518] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">Install within 1 week</span>
                <p className="text-gray-500 text-sm">Most jobs booked and completed in under 7 days</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#F5C518] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">SEAI grant handled</span>
                <p className="text-gray-500 text-sm">We do the paperwork, you get €300 back</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-4 bg-[#18181b] rounded-xl border border-[#27272a]">
            <Phone className="w-8 h-8 text-[#F5C518]" />
            <div>
              <p className="text-gray-400 text-sm">Prefer to talk?</p>
              <a 
                href={PHONE_LINK}
                onClick={() => trackPhoneClick('cta_section')}
                className="text-white font-bold text-lg hover:text-[#F5C518] transition-colors"
                data-testid="cta-call-btn"
              >
                {PHONE}
              </a>
            </div>
          </div>
        </div>
        
        <div id="quote-form" className="scroll-mt-24">
          <LeadForm />
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-8 bg-[#0f0f10] border-t border-[#27272a]" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="LK Electrical & Security" className="h-8 w-auto" />
          <span className="text-gray-500 text-sm">© {new Date().getFullYear()} LK Electrical & Security</span>
        </div>
        <div className="flex items-center gap-6">
          <span className="text-gray-500 text-sm">Dublin & Surrounding Counties</span>
          <a 
            href={PHONE_LINK}
            onClick={() => trackPhoneClick('footer')}
            className="flex items-center gap-2 text-[#F5C518] font-semibold hover:text-[#FFD54F] transition-colors"
            data-testid="footer-phone"
          >
            <Phone className="w-4 h-4" />
            {PHONE}
          </a>
        </div>
      </div>
    </div>
  </footer>
);

function LandingPage() {
  return (
    <div className="App bg-[#0f0f10] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <TrustSection />
        <WhatsIncluded />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
